import React from 'react';
import { GetServerSideProps } from 'next';
import { useQuery } from 'urql';

import Breadcrumbs from '@/components/Breadcrumbs';
import CardGrid from '@/components/CardGrid';
import HeroImageFit from '@/components/HeroImageFit';
import HeroImage from '@/components/layout/HeroImage';
import { useIsMobile } from '@/hooks';
import useStore from '@/hooks/useStore';
import { renderPageContent } from '@/lib/ContentRenderer';
import { getServerSidePropsWithGlobalData } from '@/lib/getServerSideProps';
import { extractFragment, extractItems } from '@/lib/GraphQLHelper';
import { withUrql } from '@/lib/urqlClient';
import { ErrorPage } from '@/pages/_error';

import query from './query.graphql';
import related from './related.graphql';

import styles from './styles.module.scss';

interface Props {
  slug: string;
}

const NewsArticle = ({ slug }: Props) => {
  const [isMobile] = useIsMobile(768);
  const { exclusive } = useStore('userStore');
  const [{ data, error, fetching }] = useQuery<NewsEntryQuery>({
    query,
    variables: {
      slug,
    },
  });

  const newsEntry = extractFragment<NewsEntryFragment>(data?.entry);
  const interestEntries = extractItems<InterestFragment>(newsEntry?.relatedInterests);
  const pageContent = extractItems<Craft.PageContent>(newsEntry?.pageContent);

  const interests = interestEntries?.map((i) => i?.slug).toString() || '*';

  const [{ data: relatedData }] = useQuery<NewsRelatedQuery>({
    query: related,
    variables: {
      id: ['not', newsEntry?.id],
      exclusive,
      search: `${interests && `relatedInterests:${interests}`}`,
    },
  });

  if (error) {
    return <ErrorPage statusCode={500} err={error} />;
  }

  if (!fetching && !newsEntry) {
    return <ErrorPage statusCode={404} />;
  }

  const breadcrumbs: BreadcrumbItem[] = [{ title: 'News & Events', uri: '/news' }, { title: newsEntry?.title || '' }];

  return (
    <article className={styles.newsArticle} data-testid="news-page">
      <HeroImage image={newsEntry?.heroImage?.[0]?.url}>
        <div className={styles.heroContent}>
          <div className={styles.body}>
            <strong>News &amp; Events</strong>
            <h3>{newsEntry?.title}</h3>
            <div className={styles.breadcrumbs}>
              <Breadcrumbs breadcrumbs={breadcrumbs} />
            </div>
            <div dangerouslySetInnerHTML={{ __html: newsEntry?.body || '' }} />
          </div>
          {newsEntry?.heroImage?.[0]?.url && <HeroImageFit src={newsEntry.heroImage[0].url} />}
        </div>
      </HeroImage>
      <div className={styles.pageContent}>{pageContent?.map((row) => renderPageContent(row!))}</div>
      <div className={styles.relatedContent}>
        {relatedData?.relatedNews && (
          <div className={styles.relatedNews}>
            <CardGrid
              title="Related articles"
              cards={relatedData.relatedNews as NewsEntryFragment[]}
              slider={isMobile}
              small={isMobile}
            />
          </div>
        )}
        {relatedData?.relatedProduct && (
          <div className={styles.relatedOffers}>
            <CardGrid
              title="You might be interested in these offers"
              cards={relatedData?.relatedProduct as ETicketFragment[]}
              slider={isMobile}
              small={isMobile}
            />
          </div>
        )}
      </div>
    </article>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) =>
  await getServerSidePropsWithGlobalData(ctx, async (ctx) => {
    const { urqlClient, nextCtx } = ctx;
    const slug = nextCtx?.query.slug;

    await urqlClient
      .query<NewsEntryQuery, NewsEntryQueryVariables>(query, {
        slug,
      })
      .toPromise();

    return { props: { slug } };
  });

export default withUrql(NewsArticle);
